import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'

import { PromptProviderV2, withPromptV2 } from '@acorns/web-utils'
import debug from 'debug'
import { addIndex, map } from 'ramda'
import {
  branch,
  compose,
  createSink,
  getContext,
  renderComponent,
  withContext,
  withState,
} from 'recompose'

import AlertBadgePath from 'assets/icons/alert-badge.png'
import ConfirmationModal, {
  ButtonStyle,
  ModalActionTypes,
} from 'components/ConfirmationModal'

import CustomModal, {
  ModalAction,
} from '../pages/spend/screens/signup/address/custom-confirmation-modal'

export type Prompt = (data: PromptConfig) => Promise<UserSelection>

type Props = {
  prompt?: Prompt
}

type UserSelection = {
  handler: (payload?: any) => void
  index: number
  label: string
  type: ModalActionTypes
}

type State = {
  actions?: ModalAction[]
  additionalHeader?: string
  additionalMessage?: string | React.ReactNode | (() => React.ReactNode)
  additionalTitle?: string
  body: string | React.ReactNode | (() => React.ReactNode)
  button?: string
  buttonStyle?: ButtonStyle
  eventFields?: object // for passing segment analytics event fields
  header: string
  image?: string
  imageAlt?: string
  imageBorder?: boolean
  show: boolean
  testId?: string
  closeOnOutsideClick?: boolean
  custom?: boolean
  prompted: boolean
  imageMax?: 'none' | 'large'
}

interface Image {
  src: string
  alt: string
}

export type PromptConfig = {
  actions?: ModalAction[]
  additionalHeader?: string
  additionalMessage?: string | React.ReactNode | (() => React.ReactNode)
  additionalTitle?: string
  button?: string
  buttonStyle?: ButtonStyle
  eventFields?: object
  image?: Image | null
  imageBorder?: boolean
  message: string | React.ReactNode | (() => React.ReactNode)
  title: string
  testId?: string
  closeOnOutsideClick?: boolean
  custom?: boolean
  imageMax?: 'none' | 'large'
}

const mapIndexed = addIndex(map)

// Registry for graphql error handlers
export class Provider extends PureComponent<Props, State> {
  public static childContextTypes = {
    promptUser: PropTypes.func.isRequired,
    promptActionTypes: PropTypes.object.isRequired,
    prompted: PropTypes.bool,
    closeModal: PropTypes.func.isRequired,
  }

  protected log: debug.Debugger
  protected pending: { resolve: any; reject: any } | null

  constructor(props: Props) {
    super(props)

    this.pending = null

    // Note: Setup an internal logger
    this.log = debug('prompt')
    // Note: Setup our initial state
    this.state = {
      show: false,
      body: 'It looks like something went wrong.',
      header: 'Oops!',
      image: undefined,
      button: 'Ok',
      prompted: false,
    }
  }

  get actions(): ModalAction[] {
    if (this.state.button) {
      return [
        {
          type: ModalActionTypes.primary,
          label: this.state.button || 'Ok',
          handler: this.onAcknowledge as any, // TYPEFIX_TODO: this could be broken
        },
      ]
    } else if (this.state.actions) {
      return mapIndexed<any, any>((action: ModalAction, index: number) => {
        const payload = {
          index,
          ...action,
        }

        return {
          ...action,
          handler: this.onAcknowledge.bind(this, payload),
        }
      }, this.state.actions) as ModalAction[]
    } else {
      throw new Error('Prompt must receive "button" or "actions" config')
    }
  }

  public getChildContext() {
    const { promptUser, closeModal } = this
    const { prompt } = this.props
    const { prompted } = this.state

    return {
      promptActionTypes: ModalActionTypes,
      promptUser: prompt || promptUser,
      prompted,
      closeModal,
    }
  }

  public promptUser = (config: PromptConfig) => {
    if (this.state.show === true) {
      this.log(
        'Attempted to prompt user when prompt is already showing. This is a NO-OP.',
      )
      return Promise.resolve()
    }

    this.setState({
      actions: config.actions,
      additionalHeader: config.additionalHeader,
      additionalMessage: config.additionalMessage,
      additionalTitle: config.additionalTitle,
      imageMax: config.imageMax,
      body: config.message,
      button: config.button,
      buttonStyle: config.buttonStyle,
      eventFields: config.eventFields,
      header: config.title,
      image: config.image === undefined ? AlertBadgePath : config.image?.src,
      imageAlt: config.image?.alt || '',
      imageBorder: config.imageBorder,
      testId: config.testId,
      show: true,
      closeOnOutsideClick: config.closeOnOutsideClick,
      custom: config.custom,
      prompted: true,
    })

    return new Promise((resolve, reject) => {
      this.pending = { resolve, reject }
    })
  }

  public closeModal = () => {
    this.setState({ show: false, prompted: false })
  }

  public render() {
    return (
      <>
        {this.props.children}

        {this.state.custom ? (
          <CustomModal
            actions={this.actions}
            additionalBody={this.state.additionalMessage}
            additionalHeader={this.state.additionalHeader}
            additionalTitle={this.state.additionalTitle}
            body={this.state.body}
            buttonStyle={this.state.buttonStyle}
            eventFields={this.state.eventFields}
            header={this.state.header}
            image={this.state.image}
            isOpen={this.state.show}
            data-testid={this.state.testId}
            closeOnOutsideClick={true}
            onRequestClose={() =>
              this.state.closeOnOutsideClick
                ? this.setState({ show: false })
                : null
            }
          />
        ) : (
          <ConfirmationModal
            actions={this.actions}
            body={this.state.body}
            buttonStyle={this.state.buttonStyle}
            eventFields={this.state.eventFields}
            header={this.state.header}
            image={this.state.image}
            imageAlt={this.state.imageAlt}
            imageBorder={this.state.imageBorder}
            isOpen={this.state.show}
            data-testid={this.state.testId}
            imageMax={this.state.imageMax}
            closeOnOutsideClick={this.state.closeOnOutsideClick}
            onRequestClose={() =>
              this.state.closeOnOutsideClick
                ? this.setState({ show: false })
                : null
            }
          />
        )}
      </>
    )
  }

  private onAcknowledge = (event: any) => {
    this.setState({ show: false })
    this.pending?.resolve(event)
    this.pending = null
  }
}

export const withPromptV1 = getContext({
  promptActionTypes: PropTypes.object,
  promptUser: PropTypes.func,
  prompted: PropTypes.bool,
  closeModal: PropTypes.func,
})

export type PromptProps = {
  promptActionTypes: ModalActionTypes
  promptUser: Prompt
  prompted: boolean
  closeModal: () => void
}

export const enum API {
  v1,
  v2,
}

type Context = {
  api: API
  setApi: (api: API) => void
}

type ChangeApiProps = Context & PromptV1Props

// prompt is so people can mock promptUser in tests
type PromptV1Props = { prompt?: Prompt }

const ContextProp = {
  api: PropTypes.number,
  setApi: PropTypes.func,
}

const enhance = compose<ChangeApiProps, PromptV1Props>(
  withState('api', 'setApi', API.v1),
  withContext<Context, Context>(ContextProp as any, ({ api, setApi }) => ({
    api,
    setApi,
  })),
)

const ChooseApiVersion = (api: API) =>
  createSink((({ setApi }: Context) => setApi(api)) as any)

const PromptAdapter = enhance(({ api, children, prompt }) => {
  const PromptProvider = api === API.v1 ? Provider : PromptProviderV2

  return <PromptProvider prompt={prompt}>{children}</PromptProvider>
})

const withPrompt = <Inner extends {} = {}, Outer extends {} = {}>(
  selectedAPI = API.v1,
) =>
  compose<Inner, Outer>(
    getContext(ContextProp),
    branch(
      ({ api }: Context) => api !== selectedAPI,
      renderComponent(ChooseApiVersion(selectedAPI)),
    ),
    branch(
      ({ api }: Context) => api === API.v1,
      withPromptV1 as any,
      withPromptV2 as any,
    ),
  )

export { ButtonStyle, ModalActionTypes, PromptAdapter, withPrompt }
